import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface HeroContent {
  badge: string;
  headline1: string;
  headline2: string;
  headline3: string;
  subtext: string;
  bgImage: string;
  ctaPrimary: string;
  ctaSecondary: string;
}

export interface AboutContent {
  heading1: string;
  heading2: string;
  para1: string;
  para2: string;
  vision: string;
  mission: string;
  image: string;
}

export interface ServiceItem {
  icon: string;
  category: string;
  title: string;
  description: string;
  items: string[];
  image: string;
}

export interface TeamContent {
  para1: string;
  para2: string;
  para3: string;
  para4: string;
  photo1: string;
  photo2: string;
}

export interface ContactContent {
  address: string;
  phone1: string;
  phone2: string;
  email: string;
}

export interface FooterContent {
  tagline: string;
  email: string;
  phone1: string;
  phone2: string;
  address: string;
}

export interface ProjectItem {
  id: string;
  title: string;
  category: string;
  description: string;
  image: string;
  client: string;
  year: string;
}

export interface SiteContent {
  hero: HeroContent;
  about: AboutContent;
  services: ServiceItem[];
  team: TeamContent;
  contact: ContactContent;
  footer: FooterContent;
  projects: ProjectItem[];
}

const defaultContent: SiteContent = {
  hero: {
    badge: 'Your Trusted Survey and Geoinformation Provider',
    headline1: 'Survey and Mapping',
    headline2: 'Geoinformation Services',
    headline3: 'Geospatial Research',
    subtext: 'Transforming geospatial data into actionable intelligence across East Africa since 2014. Over 100 many years of combined expertise in surveying, geo information and research.',
    bgImage: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/8464f114-30a5-40f8-9d1b-18e5f7801693_Royalty-Free-Stock-Photos-_-Stock-Images--Pictures---iStock.jpg?v=2ec110f7eb68773580c0b6a1e2ee5050',
    ctaPrimary: 'Get a Free Consultation',
    ctaSecondary: 'Explore Our Services',
  },
  about: {
    heading1: 'Bridging Technology and Geomatics',
    heading2: 'for Development',
    para1: 'GeoEng Systems was established in October 2014 and offers a host of innovative Geoinformation, Research and IT services. Our combined skills and experience includes more than 100 man-years covering ICT, Research, Geomatics, Geoinformatics and more.',
    para2: 'Our key strength that differentiates us from the competition is the modern, immense and multi-disciplinary knowledge and skills we possess. We recruit the best qualified staff and provide rigorous induction and regular training to ensure they deliver customer expectations.',
    vision: 'To be the leading provider of high quality Geoinformation, Research and IT Services in East Africa and beyond.',
    mission: 'To help our customers simplify their business processes through Geoinformation, Research and IT services.',
    image: 'https://readdy.ai/api/search-image?query=professional%20African%20engineers%20and%20GIS%20specialists%20working%20with%20large%20digital%20maps%20and%20satellite%20imagery%20on%20multiple%20monitors%20in%20a%20bright%20modern%20office%20Nairobi%20Kenya%20geospatial%20technology%20company%20warm%20natural%20light%20collaborative%20environment%20high%20quality%20photography&width=700&height=600&seq=about002&orientation=landscape',
  },
  services: [
    {
      icon: 'ri-map-2-line',
      category: 'GIS & Surveying',
      title: 'Geo-information Services',
      description: 'Comprehensive geospatial solutions including cadastral surveys, topographic mapping, aerial photogrammetry, and remote sensing across East Africa.',
      items: ['Cadastral & Boundary Surveys', 'Topographic & Engineering Surveys', 'Aerial Photogrammetry & Remote Sensing', 'Control Survey Networks', 'GIS Data Modelling & Analysis'],
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/773a7af1-dbd9-4999-8e52-79edd1f6da2c_download-6.jpg?v=bbec6a216c675f2b8700fda61d4623cd',
    },
    {
      icon: 'ri-server-line',
      category: 'Information Technology',
      title: 'IT Services',
      description: 'End-to-end technology solutions from cloud infrastructure and mobile apps to enterprise resource planning and big data analytics.',
      items: ['Cloud Services (AWS, Google Cloud)', 'Mobile App Development', 'Enterprise Resource Planning (ERP)', 'Big Data & Analytics', 'Software Development & IT Consultancy'],
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/2ba4b606-67cd-46f5-8e7e-0e31f0210cf1_Ai-learning-and-artificial-intelligence-concept_-_-Premium-Photo.jpg?v=b2e5380cccf49d3aa6d8cdba1aca90e8',
    },
    {
      icon: 'ri-bar-chart-2-line',
      category: 'Research & Analytics',
      title: 'Research Services',
      description: 'Rigorous data collection, analysis, and impact evaluation using industry-leading tools for health, agriculture, climate, and socio-economic research.',
      items: ['Socio-Economic & Financial Surveys', 'Environmental Impact Assessment (EIA)', 'CAPI Tools (ODK, KoBoToolbox)', 'R, SPSS, STATA, EViews Analysis', 'Training & Capacity Building'],
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/5c1e058c-bfc1-4090-9870-7bd29ecbdb7b_Best-Libraries-and-Platforms-for-Data-Visualization.jpg?v=c5f779cdc5e10343082dcbd64722e8cb',
    },
  ],
  team: {
    para1: 'GeoEng Systems has a team of highly qualified and experienced staff. We only recruit π (pi) candidates — professionals with experience or training in more than two of our principal disciplines.',
    para2: 'Our combined skills and experience includes more than 100 man-years covering ICT, Research, Geomatics, Geoinformatics, Project Management, Monitoring & Evaluation, Statistics, and more.',
    para3: 'Our key strength that differentiates us from the competition is the modern, immense and multi-disciplinary knowledge and skills we possess. We recruit the best qualified staff and provide rigorous induction and regular training to ensure they deliver customer expectations.',
    para4: 'All our staff are required to be members of relevant professional bodies and to keep up to date with the latest developments in their respective fields. This ensures that our clients always receive the most current and relevant advice and services.',
    photo1: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/49fb4714-7862-49ee-a73a-3dc312983163_IMG_2620.jpg?v=178a6f64e33e45ecc84f6695affa9dda',
    photo2: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/40737545-732a-4992-8e85-6c8caf054f10_IMG_2621.jpg?v=a03f2a40b15b2189450819cce08b76a5',
  },
  contact: {
    address: 'Consolidated Bank House, Koinange Street\n4th Floor, Room 11\nNairobi, Kenya',
    phone1: '+254 20 239 3556',
    phone2: '+254 722 594 540',
    email: 'info@geoeng.co.ke',
  },
  footer: {
    tagline: 'Geoinformation · Research · IT\nMapping Tomorrow\'s Infrastructure Today.',
    email: 'info@geoeng.co.ke',
    phone1: '+254 20 239 3556',
    phone2: '+254 722 594 540',
    address: 'Consolidated Bank House, Koinange Street\n4th Floor, Room 11\nNairobi, Kenya',
  },
  projects: [
    {
      id: '1',
      title: 'GIS & Remote Sensing Product Demonstration',
      category: 'GIS & Surveying',
      description: 'Hands-on demonstration of cutting-edge GIS and remote sensing products and technologies in Libreville, Gabon — showcasing satellite imagery analysis, spatial data processing, and geospatial solutions for regional stakeholders.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/e135bf64-39c6-4dba-9f53-a4b9635ae012_IMG_2680.jpg?v=551f54a722a3db97919983b79aca3cc2',
      client: 'Libreville, Gabon',
      year: '2023',
    },
    {
      id: '2',
      title: 'Establishing Control Survey for Topographic Survey Project',
      category: 'GIS & Surveying',
      description: 'Establishment of geodetic control survey networks to support a comprehensive topographic survey project in Vihiga County, Kenya — providing precise reference points for accurate mapping and land administration.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/1395692f-9e54-4213-a73b-7c542182eee8_IMG_2681.jpg?v=d8da08f7550e664321ede4e49cab1d69',
      client: 'Vihiga County, Kenya',
      year: '2022',
    },
    {
      id: '3',
      title: 'GIS & Remote Sensing Training — Mercy Corps',
      category: 'Training & Capacity Building',
      description: 'Intensive GIS and remote sensing training programme delivered at the Mercy Corps office in Addis Ababa, Ethiopia — equipping field staff and programme teams with practical geospatial skills for humanitarian and development work.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/bbb505e3-e7bd-467a-bb3c-cc7f53ffde91_IMG_2682.jpg?v=c206d9c73f7f4dd2f5b874c5ad4b9d30',
      client: 'Mercy Corps, Addis Ababa, Ethiopia',
      year: '2021',
    },
    {
      id: '4',
      title: 'Topographic Survey — Daykio Plantation Ltd',
      category: 'GIS & Surveying',
      description: 'Detailed topographic survey for Daykio Plantation Ltd, delivering high-accuracy terrain data, contour mapping, and spatial datasets to support plantation planning, management, and infrastructure development.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/8486ca16-c2a5-4a3b-aab5-8f3a887d476e_IMG_2683.jpg?v=7b29fe5a7f249b153749cdc3c311161e',
      client: 'Daykio Plantation Ltd',
      year: '2021',
    },
    {
      id: '5',
      title: 'Data Collection — Belgian Technical Cooperation Project',
      category: 'Research & Evaluation',
      description: 'Field data collection and geospatial survey support for a Belgian Technical Cooperation (BTC/Enabel) development project in Tanzania — gathering critical socio-economic and spatial data to inform project planning and implementation.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/b7681741-b4a8-4ad9-b88e-9018ff424f18_IMG_2685.jpg?v=70bbc0dc8340801f3edcce75bc387db1',
      client: 'Belgian Technical Cooperation (BTC), Tanzania',
      year: '2020',
    },
    {
      id: '6',
      title: 'Data Modelling for a Digital Land Information System',
      category: 'GIS & Surveying',
      description: 'Comprehensive data modelling and database design for a Digital Land Information System (DLIS) in Kenya — structuring spatial and attribute data to support land registration, administration, and management across multiple counties.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/623cdd92-0654-4195-bcbc-a470bfb78954_IMG_2684.jpg?v=f4d9a8e2e1c891425d312352b4fdcfbf',
      client: 'Kenya',
      year: '2020',
    },
    {
      id: '7',
      title: 'Field Survey & Geospatial Data Collection',
      category: 'GIS & Surveying',
      description: 'On-site field survey and geospatial data collection exercise — capturing precise spatial measurements, ground-truth data, and site conditions to support mapping, planning, and infrastructure development projects.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/e8057a1c-56fa-4593-b466-20c0ee27136e_IMG_8324.jpeg?v=f3c81e52265a162dc2ad477ac89a5760',
      client: 'GeoEng Systems',
      year: '2024',
    },
    {
      id: '8',
      title: 'Surveying and Mapping',
      category: 'GIS & Surveying',
      description: 'Comprehensive geospatial research and site assessment project — combining field observations, spatial analysis, and data collection to deliver actionable insights for land use planning and environmental management.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/4e00a5d1-2af1-40d8-a45b-6c4388f45230_IMG_8336.jpeg?v=516352b5144dfbdaafad333fba578710',
      client: 'GeoEng Systems',
      year: '2024',
    },
    {
      id: '9',
      title: 'Field Operations & Survey Deployment',
      category: 'GIS & Surveying',
      description: 'Field operations and survey deployment project — mobilising specialist teams and equipment for on-the-ground data capture, boundary demarcation, and spatial verification across project sites.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/38298ab6-bf83-49d5-9cfc-fc3f93dbdbbb_PHOTO-2025-10-27-14-43-13.jpg?v=36658ad8cd1dec7bd5e0edfbb6af3cb1',
      client: 'GeoEng Systems',
      year: '2025',
    },
    {
      id: '10',
      title: 'Geospatial Field Survey & Data Capture',
      category: 'GIS & Surveying',
      description: 'Comprehensive field survey and geospatial data capture exercise — deploying specialist survey teams to collect high-accuracy spatial measurements, ground control points, and site data to support mapping and infrastructure planning.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/fe11ebb5-8f8c-4e9a-b316-eadbab1d1810_b4caf1f1-cb88-44f7-b2f2-5ddc24496491.jpg?v=ecdcd49f1fdba9898d8edcb877a2d199',
      client: 'GeoEng Systems',
      year: '2025',
    },
    {
      id: '11',
      title: 'Topographic Mapping & Site Assessment',
      category: 'GIS & Surveying',
      description: 'Detailed topographic mapping and site assessment project — capturing terrain data, elevation profiles, and spatial features to support land use planning, engineering design, and environmental management.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/4347711c-a06d-4a25-bf30-48a38c75187b_b17fae48-3c88-4ff4-97be-7b5de2115407.jpg?v=890599d696be77cdb65c4870c27128d5',
      client: 'GeoEng Systems',
      year: '2025',
    },
    {
      id: '12',
      title: 'Boundary Demarcation & Land Survey',
      category: 'GIS & Surveying',
      description: 'Precision boundary demarcation and land survey project — establishing accurate property boundaries, survey beacons, and spatial records to support land administration, registration, and dispute resolution.',
      image: 'https://storage.readdy-site.link/project_files/fe05d92d-53b5-4c90-ab41-873201f19767/8f3f5ef3-fc7c-409f-9402-0f4527a94e64_ab03d7d9-40e8-4d23-a564-079e07466767.jpg?v=3adee8ebe22a2a771c74e43ef0ee2b47',
      client: 'GeoEng Systems',
      year: '2025',
    },
  ],
};

interface SiteContentContextType {
  content: SiteContent;
  updateContent: (path: string, value: unknown) => void;
  resetContent: () => void;
}

const SiteContentContext = createContext<SiteContentContextType | null>(null);

function setNestedValue(obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  const keys = path.split('.');
  const result = { ...obj };
  let current: Record<string, unknown> = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    const idx = parseInt(keys[i + 1], 10);
    if (!isNaN(idx)) {
      current[key] = [...(current[key] as unknown[])];
      current = current[key] as Record<string, unknown>;
    } else {
      current[key] = { ...(current[key] as Record<string, unknown>) };
      current = current[key] as Record<string, unknown>;
    }
  }
  current[keys[keys.length - 1]] = value;
  return result;
}

export function SiteContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContent>(() => {
    try {
      const stored = localStorage.getItem('geoeng_site_content');
      if (stored) {
        const parsed = JSON.parse(stored);
        // If any cached project still has an AI-generated image URL, reset all projects to defaults
        const hasAiImages =
          parsed.projects &&
          parsed.projects.some((p: ProjectItem) => typeof p.image === 'string' && p.image.includes('readdy.ai/api/search-image'));
        const hasOldSampleProjects =
          parsed.projects &&
          parsed.projects.length === 6 &&
          parsed.projects[0]?.title === 'Cadastral Survey — Nairobi Metropolitan';
        const isMissingNewProjects =
          parsed.projects &&
          (!parsed.projects.some((p: ProjectItem) => p.id === '6') ||
           !parsed.projects.some((p: ProjectItem) => p.id === '7') ||
           !parsed.projects.some((p: ProjectItem) => p.id === '8') ||
           !parsed.projects.some((p: ProjectItem) => p.id === '9') ||
           !parsed.projects.some((p: ProjectItem) => p.id === '10') ||
           !parsed.projects.some((p: ProjectItem) => p.id === '11') ||
           !parsed.projects.some((p: ProjectItem) => p.id === '12') ||
           parsed.projects.some((p: ProjectItem) => p.id === '8' && p.category !== 'GIS & Surveying'));
        // Force refresh if cached project images are missing but defaults now have them
        const isMissingImages =
          parsed.projects &&
          parsed.projects.some((p: ProjectItem, idx: number) => {
            const def = defaultContent.projects[idx];
            return def && def.image && !p.image;
          });
        // Reset hero if it still has old headline or old man-years subtext or old badge/headlines
        const hasOldHeadline = parsed.hero?.headline1 === 'Precision Mapping';
        const hasOldBadge =
          parsed.hero?.badge === "East Africa's Leading GeoInformation Partner";
        const hasOldHeadline2 =
          parsed.hero?.headline2 === 'Advanced IT Solutions' ||
          parsed.hero?.headline3 === 'Research Excellence';
        const hasOldSubtext =
          parsed.hero?.subtext &&
          (parsed.hero.subtext.includes('man-years') ||
           parsed.hero.subtext.includes('GIS, IT, and research') ||
           parsed.hero.subtext.includes('many yeas'));
        // Reset about if it still has old "Geography" heading
        const hasOldAboutHeading =
          parsed.about?.heading1 &&
          (parsed.about.heading1.includes('Geography') ||
           !parsed.about.heading1.includes('Geomatics'));
        // Reset contact/footer if address is still the old P.O. Box
        const hasOldAddress =
          parsed.contact?.address?.includes('P.O. Box') ||
          parsed.footer?.address?.includes('P.O. Box');
        return {
          ...defaultContent,
          ...parsed,
          hero: (hasOldHeadline || hasOldSubtext || hasOldBadge || hasOldHeadline2) ? defaultContent.hero : (parsed.hero ?? defaultContent.hero),
          about: hasOldAboutHeading ? defaultContent.about : (parsed.about ?? defaultContent.about),
          contact: hasOldAddress ? defaultContent.contact : (parsed.contact ?? defaultContent.contact),
          footer: hasOldAddress ? defaultContent.footer : (parsed.footer ?? defaultContent.footer),
          projects: (hasAiImages || hasOldSampleProjects || isMissingNewProjects || isMissingImages)
            ? defaultContent.projects
            : (parsed.projects ?? defaultContent.projects),
        };
      }
    } catch { /* ignore */ }
    return defaultContent;
  });

  useEffect(() => {
    localStorage.setItem('geoeng_site_content', JSON.stringify(content));
  }, [content]);

  const updateContent = (path: string, value: unknown) => {
    setContent((prev) => {
      const updated = setNestedValue(prev as unknown as Record<string, unknown>, path, value);
      return updated as unknown as SiteContent;
    });
  };

  const resetContent = () => {
    setContent(defaultContent);
    localStorage.removeItem('geoeng_site_content');
  };

  return (
    <SiteContentContext.Provider value={{ content, updateContent, resetContent }}>
      {children}
    </SiteContentContext.Provider>
  );
}

export function useSiteContent() {
  const ctx = useContext(SiteContentContext);
  if (!ctx) throw new Error('useSiteContent must be used within SiteContentProvider');
  return ctx;
}
